<template>
  <div class="email-identity">
    <p class="email-identity-lede">
      Festival records across every year (enrollments, canceled deposits, history) follow this person.
      Linking an old address attaches that history here. Changing the primary email updates those records.
      This does <strong>not</strong> change their Firebase / Broche login — they still sign in with the password email they already use.
    </p>

    <p v-if="loading" class="muted small">Loading person record…</p>
    <template v-else>
      <p class="email-identity-row">
        <strong>Primary festival email:</strong>
        {{ displayEmail || '—' }}
      </p>
      <p v-if="displayPrevious.length" class="email-identity-row">
        <strong>Also linked:</strong>
        {{ displayPrevious.join(', ') }}
      </p>
      <p v-else class="muted small">No other emails linked yet.</p>
    </template>

    <div class="email-identity-block">
      <v-text-field
        v-model="newPrimary"
        label="Change primary email to"
        type="email"
        hide-details
        class="mb-2"
      />
      <v-btn
        small
        color="primary"
        dark
        :loading="savingPrimary"
        :disabled="!canChangePrimary"
        @click="onChangePrimary"
      >
        Save new email
      </v-btn>
    </div>

    <div class="email-identity-block">
      <v-text-field
        v-model="aliasEmail"
        label="Link another email (same person — past records)"
        type="email"
        hide-details
        class="mb-2"
      />
      <p class="muted small">
        Use this when they registered or logged in with a new address but 2024–2026 (or a canceled deposit) is still under the old one.
      </p>
      <v-btn
        small
        outlined
        color="primary"
        :loading="savingAlias"
        :disabled="!canLinkAlias"
        @click="onLinkAlias"
      >
        Link email
      </v-btn>
    </div>

    <p v-if="status" class="email-identity-status">{{ status }}</p>
    <p v-if="error" class="email-identity-error">{{ error }}</p>
  </div>
</template>

<script>
import { normalizeEmail } from '@/utils/iabf-people'

export default {
  name: 'IabfPersonEmailIdentity',
  props: {
    personId: {
      type: String,
      default: ''
    },
    email: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: false,
      savingPrimary: false,
      savingAlias: false,
      person: null,
      newPrimary: '',
      aliasEmail: '',
      status: '',
      error: ''
    }
  },
  computed: {
    resolvedPersonId () {
      return this.person && this.person.id
        ? this.person.id
        : (this.personId || '')
    },
    displayEmail () {
      return normalizeEmail((this.person && this.person.email) || this.email)
    },
    displayPrevious () {
      const list = (this.person && this.person.previousEmails) || []
      return list.filter(Boolean)
    },
    canChangePrimary () {
      const next = normalizeEmail(this.newPrimary)
      return !!next && next !== this.displayEmail && !this.savingPrimary
    },
    canLinkAlias () {
      const alias = normalizeEmail(this.aliasEmail)
      if (!alias || !this.resolvedPersonId || this.savingAlias) {
        return false
      }
      if (alias === this.displayEmail) {
        return false
      }
      return this.displayPrevious.indexOf(alias) === -1
    }
  },
  watch: {
    personId () {
      this.loadPerson()
    },
    email () {
      if (!this.person) {
        this.loadPerson()
      }
    }
  },
  created () {
    this.loadPerson()
  },
  methods: {
    async loadPerson () {
      this.loading = true
      this.error = ''
      try {
        const person = await this.$store.dispatch('loadIabfPersonIdentity', {
          personId: this.personId || '',
          email: this.email || ''
        })
        this.person = person || null
        if (person && person.email && !this.newPrimary) {
          this.newPrimary = person.email
        } else if (!this.newPrimary && this.email) {
          this.newPrimary = this.email
        }
      } catch (e) {
        this.error = (e && e.message) || 'Could not load the person record.'
      }
      this.loading = false
    },
    async onChangePrimary () {
      const next = normalizeEmail(this.newPrimary)
      if (!next) {
        return
      }
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Change this dancer’s primary festival email to ' + next + '?\n\n' +
        'This updates enrollments and canceled records for every year, and keeps ' +
        this.displayEmail + ' as a linked alias.\n\n' +
        'If ' + next + ' already has its own person record, those records will be merged into this one.\n\n' +
        'Their Firebase login email is not changed.'
      )
      if (!ok) {
        return
      }
      this.savingPrimary = true
      this.error = ''
      this.status = ''
      try {
        const result = await this.$store.dispatch('changeIabfPersonEmail', {
          personId: this.resolvedPersonId,
          newEmail: next,
          oldEmail: this.displayEmail
        })
        this.person = result.person || this.person
        this.newPrimary = result.email || next
        this.status = 'Primary email updated. Linked: ' + (result.previousEmails || []).join(', ')
        this.$emit('updated', result)
      } catch (e) {
        this.error = (e && e.message) || 'Could not change email.'
      }
      this.savingPrimary = false
    },
    async onLinkAlias () {
      const alias = normalizeEmail(this.aliasEmail)
      if (!alias) {
        return
      }
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Link ' + alias + ' to this dancer?\n\n' +
        'Past festival records and any held deposit under that address will attach here.\n' +
        'If that email already has a person record, it will be merged into this one.\n\n' +
        'Primary email stays ' + this.displayEmail + ' unless it was empty.'
      )
      if (!ok) {
        return
      }
      this.savingAlias = true
      this.error = ''
      this.status = ''
      try {
        const result = await this.$store.dispatch('linkIabfPersonEmail', {
          personId: this.resolvedPersonId,
          aliasEmail: alias
        })
        this.person = result.person || this.person
        this.aliasEmail = ''
        this.status = 'Linked ' + alias + '. Also linked: ' + (result.previousEmails || []).join(', ')
        this.$emit('updated', result)
      } catch (e) {
        this.error = (e && e.message) || 'Could not link email.'
      }
      this.savingAlias = false
    }
  }
}
</script>

<style scoped>
.email-identity {
  background: #f5f0eb;
  border: 1px solid #e0d6ca;
  border-radius: 6px;
  padding: 16px 18px 18px;
  margin: 0;
}
.email-identity-lede,
.email-identity-row,
.email-identity-block,
.muted {
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  line-height: 1.5;
  color: rgba(40, 26, 23, 0.75);
}
.email-identity-lede {
  margin: 0 0 12px;
}
.email-identity-row {
  margin: 0 0 6px;
  color: #281a17;
}
.email-identity-block {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e0d6ca;
}
.email-identity-status {
  margin: 12px 0 0;
  color: #2e7d32;
  font-size: 14px;
}
.email-identity-error {
  margin: 12px 0 0;
  color: #c62828;
  font-size: 14px;
}
.muted.small {
  font-size: 13px;
  margin: 6px 0 10px;
}
</style>
